import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { ReportsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { routes } from "../../configs/constants/routes";
import useQueryState from "../../hooks/useQueryState";
import JobReport from "../../models/JobReport";
import { useJobReports } from "../../networking/job-report.hooks";
import { formatRoute } from "../../utils/route.utils";

const JobReportsListPage: React.FC = () => {
	const navigate = useNavigate();

	const { t } = useTranslation();

	const [pool, setPool] = useQueryState<string>("pool", { initialValue: "" });

	const { data, isLoading } = useJobReports({
		expand: ["consultant", "job.customer"],
		pool,
	});

	const formatOrderDate = (statDate: Date, endDate: Date) => {
		return `${moment(statDate).format(
			DATE_TIME_FORMAT_TYPES.DATE
		)} \u00A0\u00A0 - \u00A0\u00A0 ${moment(endDate).format(DATE_TIME_FORMAT_TYPES.DATE)}`;
	};

	const navigateToJobJobReportDetails = (reportId?: string) => {
		const routePath = formatRoute(routes.JOB_REPORT_DETAILS, [reportId]);
		navigate(routePath);
	};

	/**
	 * Show warning if report is submitted and rating is below 3.
	 */
	function shouldShowWarning(report: JobReport) {
		if (report.rating === undefined) return false;
		return report.submitted && Number(report.rating || 0) < 3;
	}

	return (
		<>
			<Breadcrumbs
				items={ReportsBreadcrumbs}
				onChangePool={setPool}
				selectedPool={pool}
				showPoolDropdown
			/>
			<div className="p-4 px-6">
				{isLoading ? (
					<LoadingIndicator />
				) : (
					<React.Fragment>
						<TableLayout>
							<table>
								<thead>
									<tr>
										<th>{t("jobReport.period")}</th>
										<th>{t("CREATED")}</th>
										<th>{t("SUBMITTED")}</th>
										<th>{t("JOB_ID")}</th>
										<th>{t("CUSTOMER")} </th>
										<th>{t("CONSULTANT")}</th>
										<th>{t("RATING")}</th>
									</tr>
								</thead>
								<tbody>
									{data?.jobReports.map((report) => {
										return (
											<tr
												key={report.id}
												onClick={
													report.submitted
														? () => navigateToJobJobReportDetails(report.id)
														: undefined
												}
												className={`${
													!report.submitted
														? "disabled"
														: shouldShowWarning(report)
														? "warning"
														: ""
												}`}
												style={{ cursor: report.submitted ? "pointer" : "default" }}
											>
												<td>
													{formatOrderDate(
														report.period.start.start as Date,
														report.period.end.end as Date
													)}
												</td>
												<td>
													{moment(report.metadata?.created).format(DATE_TIME_FORMAT_TYPES.DATE)}
												</td>
												<td>{report.submitted ? t("YES") : "-"}</td>
												<td>{report.job?.orderNumber}</td>
												<td>{report.job?.customer?.name} </td>
												<td>
													{report.consultant?.firstName} {report.consultant?.lastName}{" "}
												</td>
												<td>
													{report?.rating === undefined
														? t("jobReport.ratingNotSet")
														: `${report.rating} / 5`}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</TableLayout>
					</React.Fragment>
				)}
			</div>
		</>
	);
};

export default JobReportsListPage;
